"use client";

import Link from "next/link";
import { useState } from "react";
import { AuthDialog } from "@/components/auth/AuthDialog";
import type { Dictionary, Locale } from "@/i18n/dictionaries";
import type { ProductCommentDto } from "@/lib/product-types";
import styles from "./productDetail.module.css";

type CommentLabels = {
  comments: string;
  addComment: string;
  collapseComment: string;
  commentPlaceholder: string;
  submit: string;
  noComments: string;
  anonymous: string;
  emptyComment: string;
  submitted: string;
  submitFailed: string;
};

type ProductCommentsPanelProps = {
  goodsId: string;
  initialComments: ProductCommentDto[];
  commentPage: number;
  commentPageSize: number;
  commentTotal: number;
  labels: CommentLabels;
  authLabels: Dictionary["authPage"];
  locale: Locale;
};

type CommentResult = {
  ok: boolean;
  status?: string;
};

function commentHref(goodsId: string, locale: Locale, page: number, pageSize: number) {
  const query = new URLSearchParams({ lang: locale, commentPage: String(page), commentPageSize: String(pageSize) });
  return `/product/${encodeURIComponent(goodsId)}?${query.toString()}`;
}

export function ProductCommentsPanel({
  goodsId,
  initialComments,
  commentPage,
  commentPageSize,
  commentTotal,
  labels,
  authLabels,
  locale
}: Readonly<ProductCommentsPanelProps>) {
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const returnUrl = `/product/${goodsId}?lang=${locale}`;

  async function submitComment() {
    const content = commentText.trim();
    if (!content) {
      setMessage(labels.emptyComment);
      return;
    }

    setSubmitting(true);
    setMessage("");
    const result: CommentResult = await fetch(`/api/products/${goodsId}/comments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ content })
    })
      .then((response) => response.json() as Promise<CommentResult>)
      .catch(() => ({ ok: false, status: "failed", message: labels.submitFailed }));

    setSubmitting(false);
    if (result.status === "auth-required") {
      setAuthOpen(true);
      setMessage(locale === "en-US" ? "Please sign in before commenting." : "请先登录后再评论。");
      return;
    }
    if (result.status === "comment-limit") {
      setMessage(locale === "en-US"
        ? "You have reached today's limit of 10 comments for this product."
        : "您今天对该商品的评论已达 10 条，请明日再试。");
      return;
    }
    if (!result.ok) {
      setMessage(labels.submitFailed);
      return;
    }

    setCommentText("");
    setExpanded(false);
    setMessage(labels.submitted);
  }

  const pageCount = Math.max(1, Math.ceil(commentTotal / commentPageSize));
  const pager = locale === "en-US"
    ? { first: "First", previous: "Previous", next: "Next", last: "Last", page: "Page", total: "total" }
    : { first: "\u9996\u9875", previous: "\u4e0a\u4e00\u9875", next: "\u4e0b\u4e00\u9875", last: "\u672b\u9875", page: "\u7b2c", total: "\u5171" };

  return (
    <section className={styles.commentList} aria-label={labels.comments}>
      <div className={styles.commentHeader}>
        <h2>{labels.comments}</h2>
        <button type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? labels.collapseComment : labels.addComment}
        </button>
      </div>
      {expanded ? (
        <div className={styles.commentComposer}>
          <textarea value={commentText} placeholder={labels.commentPlaceholder} onChange={(event) => setCommentText(event.target.value)} />
          <button type="button" disabled={submitting} onClick={submitComment}>
            {submitting ? "..." : labels.submit}
          </button>
        </div>
      ) : null}
      {message ? <p className={styles.actionMessage}>{message}</p> : null}
      {initialComments.length > 0 ? (
        initialComments.map((comment) => (
          <article key={comment.id || comment.content} className={styles.commentItem}>
            <strong>{comment.memberName || labels.anonymous}</strong>
            <p>{comment.content}</p>
            {comment.createTime ? <span>{comment.createTime}</span> : null}
          </article>
        ))
      ) : (
        <p>{labels.noComments}</p>
      )}
      <nav className={styles.commentPager} aria-label={labels.comments} data-testid="product-comment-pager">
        <span>{pager.total} {commentTotal}</span>
        <span>{pager.page} {commentPage} / {pageCount}</span>
        {commentPage > 1 ? <Link href={commentHref(goodsId, locale, 1, commentPageSize)}>{pager.first}</Link> : <span aria-disabled="true">{pager.first}</span>}
        {commentPage > 1 ? <Link href={commentHref(goodsId, locale, commentPage - 1, commentPageSize)}>{pager.previous}</Link> : <span aria-disabled="true">{pager.previous}</span>}
        {commentPage < pageCount ? <Link href={commentHref(goodsId, locale, commentPage + 1, commentPageSize)}>{pager.next}</Link> : <span aria-disabled="true">{pager.next}</span>}
        {commentPage < pageCount ? <Link href={commentHref(goodsId, locale, pageCount, commentPageSize)}>{pager.last}</Link> : <span aria-disabled="true">{pager.last}</span>}
      </nav>
      {authOpen ? (
        <AuthDialog
          locale={locale}
          labels={authLabels}
          mode="login"
          returnUrl={returnUrl}
          onClose={() => setAuthOpen(false)}
        />
      ) : null}
    </section>
  );
}
